import type { AccountInfo } from '@azure/msal-browser';
import { z } from 'zod';
import { DealerSchema } from '@/models/entities/lead.ts';

export const DealersSchema = z.array(DealerSchema).readonly();

export type Dealers = z.infer<typeof DealersSchema>;

export const HomeAccountIdSchema = z.object({
  objectId: z.uuid().readonly().brand<'ObjectId'>().readonly(),
  tenantId: z.uuid().readonly().brand<'TenantId'>().readonly(),
  toString: z.function({
    output: z.string(),
  }),
});
export const HomeAccountIdCodec = z.codec(z.string(), HomeAccountIdSchema, {
  decode: (value: string): z.input<typeof HomeAccountIdSchema> => {
    const [objectId, tenantId] = value.split('.');
    return {
      objectId,
      tenantId,
      toString: () => value,
    };
  },
  encode: (value: z.input<typeof HomeAccountIdSchema>): string =>
    `${value.objectId}.${value.tenantId}`,
});

const EmailSchema = z.email().toLowerCase().readonly();

const NameSchema = z.string().min(1, 'Name cannot be empty').readonly();

const UserNameSchema = z.string().min(1, 'UserName cannot be empty').readonly();

const PermissionSchema = z
  .string()
  .min(1, 'Permission cannot be empty')
  .readonly();

export const UserSchema = z
  .object({
    homeAccountId: HomeAccountIdCodec,
    dealerships: DealersSchema,
    name: NameSchema.optional(),
    userName: UserNameSchema.readonly(),
    email: EmailSchema.readonly(),
    permissions: z
      .array(PermissionSchema)
      .transform((values) => new Set(values))
      .readonly(),
  })
  .readonly();

export type User = z.infer<typeof UserSchema>;

export const MsalAccountCodec = z.codec(
  z.object({
    account: z.custom<AccountInfo>(),
    scopes: z.array(z.string()),
    dealerships: DealersSchema,
  }),
  UserSchema,
  {
    decode: (input) => {
      return {
        homeAccountId: input.account.homeAccountId,
        name: input.account.name,
        userName: input.account.username,
        email: input.account.username,
        permissions: input.scopes,
        dealerships: input.dealerships,
      };
    },
    encode: () => {
      throw new Error('Architecture Violation: Direct encoding is prohibited.');
    },
  },
);
